import { apiFetch } from "@/lib/api";

export interface EomOverviewRow {
  qbo_account: string;
  mv_equivalent: string;
  positive_is: "Debit" | "Credit";
}

export interface EomTargetValues {
  adjustment_and_wastage: number;
  prototypes: number;
  wip: number;
  inventory_asset: number;
  external_costs: number;
  wosl: number;
}

export interface EomQboValues {
  inventory_shrinkage: number;
  other_cos: number;
  wip: number;
  inventory_asset: number;
  eom_inventory_close: number;
  wo_labor_liability: number;
  wo_overhead_liabilities: number;
  wo_services_liability: number;
}

export interface EomJournalEntryLine {
  qbo_account: string;
  debit: number;
  credit: number;
}

export interface EomJournalEntry {
  lines: EomJournalEntryLine[];
  total_debit: number;
  total_credit: number;
}

export interface EomMonthPayload {
  month: string;
  target: EomTargetValues;
  qbo: EomQboValues;
  journal_entry: EomJournalEntry;
  saved: boolean;
  locked: boolean;
  locked_at: string | null;
  locked_by: string | null;
  qbo_je_name: string | null;
  qbo_je_url: string | null;
  magnitude_of_je: number | null;
  target_cogs: number | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface EomMonthListRow {
  reconciliation_month: string;
  locked: boolean;
  saved: boolean;
  magnitude_of_je: number | null;
  target_cogs: number | null;
  qbo_je_name: string | null;
  qbo_je_url: string | null;
  updated_at: string;
}

export const EOM_TARGET_LABELS: Record<keyof EomTargetValues, string> = {
  adjustment_and_wastage: "Adjustment and Wastage",
  prototypes: "Prototypes",
  wip: "WIP",
  inventory_asset: "Inventory Asset",
  external_costs: "External costs",
  wosl: "WOSL",
};

export const EOM_QBO_LABELS: Record<keyof EomQboValues, string> = {
  inventory_shrinkage: "Cost of Goods Sold:Inventory Shrinkage",
  other_cos: "Other Costs Of Service (COS)",
  wip: "Work In Process Inventory - MV",
  inventory_asset: "Inventory Asset",
  eom_inventory_close: "Inventory Asset:EoM Inventory Close",
  wo_labor_liability: "Work Order Labor Liability",
  wo_overhead_liabilities: "Work Order Overhead Liabilities",
  wo_services_liability: "Work Order Services Liability",
};

export function firstOfUtcMonthIso(date = new Date()): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

export function formatMonthLabel(monthIso: string): string {
  const parsed = new Date(`${monthIso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return monthIso;
  }
  return parsed.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function shiftMonthIso(monthIso: string, deltaMonths: number): string {
  const parsed = new Date(`${monthIso}T12:00:00`);
  parsed.setUTCMonth(parsed.getUTCMonth() + deltaMonths);
  return firstOfUtcMonthIso(parsed);
}

export function formatEomMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return "—";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
}

export async function fetchEomOverview(): Promise<{ rows: EomOverviewRow[] }> {
  return apiFetch<{ rows: EomOverviewRow[] }>("/api/accounting/eom-reconciliation/overview");
}

export function buildEomMonthRange(
  startMonth = "2025-12-01",
  endMonth = firstOfUtcMonthIso(new Date(Date.UTC(new Date().getUTCFullYear(), 11, 1))),
): string[] {
  const months: string[] = [];
  let cursor = startMonth;
  const endTime = new Date(`${endMonth}T12:00:00`).getTime();
  while (new Date(`${cursor}T12:00:00`).getTime() <= endTime) {
    months.push(cursor);
    cursor = shiftMonthIso(cursor, 1);
  }
  return months.reverse();
}

export async function fetchEomSavedMonths(): Promise<{ months: string[]; rows: EomMonthListRow[] }> {
  return apiFetch<{ months: string[]; rows: EomMonthListRow[] }>("/api/accounting/eom-reconciliation/months");
}

export async function fetchEomMonth(month: string): Promise<EomMonthPayload> {
  return apiFetch<EomMonthPayload>(`/api/accounting/eom-reconciliation/${encodeURIComponent(month)}`);
}

export async function saveEomMonth(
  month: string,
  payload: { target: EomTargetValues; qbo: EomQboValues },
): Promise<EomMonthPayload> {
  return apiFetch<EomMonthPayload>(`/api/accounting/eom-reconciliation/${encodeURIComponent(month)}`, {
    method: "PUT",
    body: payload,
  });
}

export async function submitEomMonth(
  month: string,
  payload: { qbo_je_name: string; qbo_je_url: string },
): Promise<EomMonthPayload> {
  return apiFetch<EomMonthPayload>(`/api/accounting/eom-reconciliation/${encodeURIComponent(month)}/submit`, {
    method: "POST",
    body: payload,
  });
}
